import React, { useState } from 'react';
import { Box, Typography, Container, Button, IconButton, Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import App20Logo from '../../components/common/App20Logo';

const PORTAL_URL = 'https://iosuser.app20.in/';
const PORTAL_PASSWORD = '123456';
const FONT = '"Plus Jakarta Sans", sans-serif';

const fieldSx = {
  bgcolor: '#EEF2EE',
  borderRadius: '16px',
  px: 2,
  py: 1.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
};

// Final funnel screen: shows the user's portal credentials (registered phone + fixed password).
export default function PortalAccessReady() {
  const [phone] = useState(() => localStorage.getItem('phone') || '');
  const [showPassword, setShowPassword] = useState(true);
  const [copiedMsg, setCopiedMsg] = useState('');

  const loginId = phone || 'Your Registered Mobile Number';

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMsg(msg);
    } catch {
      // Clipboard can be unavailable (e.g. non-secure context); nothing to do.
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#F2F5F1', py: 1.5 }}>
      <Container maxWidth="sm" sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* Dark Green Header Card */}
        <Box sx={{ bgcolor: '#0F231A', borderRadius: '24px', px: 2.5, py: 2, color: '#fff' }}>
          <Box sx={{ mb: 1.5 }}>
            <App20Logo height={26} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
            <CheckCircleIcon sx={{ color: '#C9F23C', fontSize: 24, flexShrink: 0 }} />
            <Typography sx={{ color: '#fff', fontSize: '22px', fontWeight: 700, lineHeight: 1.15, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-0.5px' }}>
              Your portal access is ready!
            </Typography>
          </Box>
         
        </Box>

        {/* Screenshot Callout */}
        <Box
          sx={{
            bgcolor: '#C9F23C',
            borderRadius: '20px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            boxShadow: '0 4px 14px rgba(201, 242, 60, 0.45)',
          }}
        >
          <Box sx={{ bgcolor: '#0F231A', borderRadius: '12px', p: 1, display: 'flex', flexShrink: 0 }}>
            <PhotoCameraOutlinedIcon sx={{ color: '#C9F23C', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#0F231A', fontSize: '15px', fontWeight: 800, lineHeight: 1.3, fontFamily: FONT }}>
              Take a screenshot of this page now!
            </Typography>
            <Typography sx={{ color: '#0F231A', fontSize: '13px', fontWeight: 600, lineHeight: 1.4, fontFamily: FONT }}>
              Get more offers anytime on this portal using these login details.
            </Typography>
          </Box>
        </Box>

        {/* Credentials Card */}
        <Box sx={{ bgcolor: '#fff', borderRadius: '24px', px: 2, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 800, fontFamily: FONT }}>
              Your portal account
            </Typography>
            <Box sx={{ bgcolor: '#E6F4EA', color: '#0F763F', borderRadius: '999px', px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 700, fontFamily: FONT }}>
              Assigned
            </Box>
          </Box>

          <Box sx={{ ...fieldSx, mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#666', fontSize: '12px', mb: 0.25, fontFamily: FONT }}>Portal Login ID</Typography>
              <Typography sx={{ color: '#0F231A', fontSize: phone ? '18px' : '14px', fontWeight: 800, fontFamily: FONT, letterSpacing: phone ? '0.5px' : 0 }}>
                {loginId}
              </Typography>
            </Box>
            <IconButton onClick={() => phone && copy(phone, 'Login ID copied')} disabled={!phone} aria-label="Copy login ID" sx={{ color: '#0F763F' }}>
              <ContentCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ ...fieldSx, mb: 1 }}>
            <Box>
              <Typography sx={{ color: '#666', fontSize: '12px', mb: 0.25, fontFamily: FONT }}>Password</Typography>
              <Typography sx={{ color: '#0F231A', fontSize: '18px', fontWeight: 800, fontFamily: FONT, letterSpacing: '1px' }}>
                {showPassword ? PORTAL_PASSWORD : '••••••'}
              </Typography>
            </Box>
            <IconButton onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} sx={{ color: '#0F763F' }}>
              {showPassword ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Box sx={{ ...fieldSx, mb: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#666', fontSize: '12px', mb: 0.25, fontFamily: FONT }}>Portal Link</Typography>
              <Typography
                component="a"
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#0F763F', fontSize: '16px', fontWeight: 800, fontFamily: FONT, textDecoration: 'underline', wordBreak: 'break-all' }}
              >
                {PORTAL_URL.replace(/^https:\/\/|\/$/g, '')}
              </Typography>
            </Box>
            <IconButton onClick={() => copy(PORTAL_URL, 'Portal link copied')} aria-label="Copy portal link" sx={{ color: '#0F763F' }}>
              <ContentCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            fullWidth
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            sx={{
              bgcolor: '#0F763F !important',
              color: '#fff',
              py: 1.25,
              borderRadius: '999px',
              fontSize: '16px',
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: 'none',
              fontFamily: FONT,
              '&:hover': {
                bgcolor: '#0d6535 !important',
                boxShadow: 'none',
              }
            }}
          >
            Open My Portal
          </Button>

          <Typography sx={{ color: '#666', fontSize: '13px', textAlign: 'center', mt: 1, fontFamily: FONT }}>
            Your portal is a separate website. Save its link to visit again.
          </Typography>
        </Box>

        {/* Come Back Card */}
        {/* <Box sx={{ bgcolor: '#fff', borderRadius: '24px', px: 2, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
            <BookmarkBorderIcon sx={{ color: '#0F763F' }} />
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 800, fontFamily: FONT }}>
              Come back anytime
            </Typography>
          </Box>
          <Typography sx={{ color: '#555', fontSize: '13px', fontWeight: 700, mb: 0.25, fontFamily: FONT }}>
            Take a screenshot of this page
          </Typography>
          <Typography sx={{ color: '#666', fontSize: '12px', lineHeight: 1.5, fontFamily: FONT }}>
            for future reference. To log in again, use your registered mobile number and password {PORTAL_PASSWORD}.
          </Typography>
        </Box> */}

      </Container>

      <Snackbar
        open={!!copiedMsg}
        autoHideDuration={2000}
        onClose={() => setCopiedMsg('')}
        message={copiedMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
